// HistogramChart — the presentational core of the value-distribution chart,
// extracted from HistogramBody so both the standalone feature chart and the
// summary card's "Distribution" view render bins identically.
//
// Pure: it takes an already-windowed array of points and renders the bars. No
// fetch, no picker, no window state — the caller owns those. The three binning
// modes (rating / all-integer / 10-bin decimal) live here so the two callers
// can't drift apart.

import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import type { FeatureConfigLike } from '../types';
import {
  BAR_CATEGORY_GAP, BAR_FILL, BAR_TICK, BAR_TOOLTIP_CURSOR, BarTooltipCard, barChartMargin,
} from './barChartStyles';

export interface TimeSeriesDataPoint { date: number; value: number; }

const DECIMAL_BINS = 10;

interface CategoricalBin {
  kind: 'cat';
  label: string;        // x-axis tick
  rangeLabel: string;   // tooltip header
  count: number;
}

interface NumericPoint {
  kind: 'num';
  x: number;
  count: number;
  rangeLabel: string;
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

interface HistogramChartProps {
  /** Points already filtered to the visible window. */
  data: TimeSeriesDataPoint[];
  config?: FeatureConfigLike | null;
  unit?: string;
  fullscreen?: boolean;
}

const HistogramChart: React.FC<HistogramChartProps> = ({
  data, config, unit, fullscreen = false,
}) => {
  const mode = useMemo<'rating' | 'integer' | 'binned' | 'empty'>(() => {
    if (data.length === 0) return 'empty';
    if (config?.type === 'rating' || config?.type === 'labeled_rating') return 'rating';
    if (data.every((d) => Number.isInteger(d.value))) return 'integer';
    return 'binned';
  }, [data, config]);

  const ratingBins = useMemo<CategoricalBin[]>(() => {
    if (mode !== 'rating') return [];
    const values = data.map((d) => d.value);
    const max = config?.params?.max ?? Math.max(5, Math.ceil(Math.max(...values)));
    const labels = config?.type === 'labeled_rating' ? config?.params?.labels : undefined;
    const out: CategoricalBin[] = [];
    for (let i = 1; i <= max; i++) {
      const tickLabel = labels?.[i - 1] ?? String(i);
      const rangeLabel = labels?.[i - 1] ? `${i} — ${labels[i - 1]}` : `${i}`;
      out.push({ kind: 'cat', label: tickLabel, rangeLabel, count: 0 });
    }
    for (const v of values) {
      const idx = Math.min(Math.max(Math.round(v) - 1, 0), out.length - 1);
      out[idx].count++;
    }
    return out;
  }, [mode, data, config]);

  const integerPoints = useMemo<NumericPoint[]>(() => {
    if (mode !== 'integer') return [];
    const counts = new Map<number, number>();
    for (const d of data) counts.set(d.value, (counts.get(d.value) ?? 0) + 1);
    return [...counts.entries()]
      .map(([x, count]) => ({
        kind: 'num' as const,
        x,
        count,
        rangeLabel: `${x}${unit ? ' ' + unit : ''}`,
      }))
      .sort((a, b) => a.x - b.x);
  }, [mode, data, unit]);

  const binnedBins = useMemo<CategoricalBin[]>(() => {
    if (mode !== 'binned') return [];
    const values = data.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) {
      return [{
        kind: 'cat',
        label: formatNumber(min),
        rangeLabel: `${formatNumber(min)}${unit ? ' ' + unit : ''}`,
        count: values.length,
      }];
    }
    const width = (max - min) / DECIMAL_BINS;
    const out: CategoricalBin[] = [];
    for (let i = 0; i < DECIMAL_BINS; i++) {
      const lo = min + i * width;
      const hi = i === DECIMAL_BINS - 1 ? max : min + (i + 1) * width;
      out.push({
        kind: 'cat',
        label: formatNumber(lo),
        rangeLabel: `${formatNumber(lo)} – ${formatNumber(hi)}${unit ? ' ' + unit : ''}`,
        count: 0,
      });
    }
    for (const v of values) {
      let idx = Math.floor((v - min) / width);
      if (idx >= DECIMAL_BINS) idx = DECIMAL_BINS - 1;
      if (idx < 0) idx = 0;
      out[idx].count++;
    }
    return out;
  }, [mode, data, unit]);

  const renderTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ value: number; payload: { rangeLabel?: string } }> }) => {
    if (!active || !payload || payload.length === 0) return null;
    const value = payload[0].value;
    const rangeLabel = payload[0].payload?.rangeLabel ?? '';
    return (
      <BarTooltipCard>
        {rangeLabel && <div className="text-gray-600">{rangeLabel}</div>}
        <div className="text-gray-900">{value} checkin{value === 1 ? '' : 's'}</div>
      </BarTooltipCard>
    );
  };

  if (mode === 'empty') {
    return (
      <div className="flex items-center justify-center h-full text-center text-gray-500">
        <p>No values in this period.</p>
      </div>
    );
  }

  const commonMargin = barChartMargin(fullscreen, 'numeric');

  if (mode === 'integer') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={integerPoints} margin={commonMargin}>
          <XAxis
            type="number"
            dataKey="x"
            domain={['dataMin', 'dataMax']}
            padding={{ left: 16, right: 16 }}
            tick={BAR_TICK}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis allowDecimals={false} tick={BAR_TICK} tickLine={false} />
          <Tooltip cursor={BAR_TOOLTIP_CURSOR} content={renderTooltip} />
          <Bar dataKey="count" fill={BAR_FILL} isAnimationActive={false} maxBarSize={10} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  const bins: CategoricalBin[] = mode === 'rating' ? ratingBins : binnedBins;
  // Thin the x-axis labels to ~6 max rather than labelling every bin — the
  // analog of LineChartBody's ~5-tick rule. `interval` is recharts' skip count
  // between labelled ticks (0 = label all); all bars still render, only the
  // labels thin, so a 10-bin decimal histogram stops reading as a wall. In
  // fullscreen there's room for every label, so don't thin there.
  const labelInterval = !fullscreen && bins.length > 6 ? Math.ceil(bins.length / 6) - 1 : 0;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={bins} margin={commonMargin} barCategoryGap={BAR_CATEGORY_GAP}>
        <XAxis
          dataKey="label"
          tick={BAR_TICK}
          tickLine={false}
          angle={bins.length > 6 ? -30 : 0}
          textAnchor={bins.length > 6 ? 'end' : 'middle'}
          interval={labelInterval}
          height={bins.length > 6 ? 60 : 30}
        />
        <YAxis allowDecimals={false} tick={BAR_TICK} tickLine={false} />
        <Tooltip cursor={BAR_TOOLTIP_CURSOR} content={renderTooltip} />
        <Bar dataKey="count" fill={BAR_FILL} isAnimationActive={false} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default HistogramChart;
